import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, Award, BookOpen, Briefcase, Sparkles, Brain, Target, Shield, Zap, ArrowRight, CheckCircle, Rocket, BarChart 
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/about';

const AboutPage = () => {
  const { language } = useLanguage();
  const t = translations[language];

  return (
    <div className="bg-background">
      {/* Hero Section */}
      <div className="relative bg-background overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 to-indigo-900/20 z-0"></div>
        <div className="max-w-7xl mx-auto">
          <div className="relative z-10 pb-8 bg-background sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
            <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
              <div className="sm:text-center lg:text-left">
                <h1 className="text-4xl tracking-tight font-extrabold text-foreground sm:text-5xl md:text-6xl">
                  <span className="block">{t.hero.title}</span>
                  <span className="block text-accent glow-text">{t.hero.subtitle}</span>
                </h1>
                <p className="mt-3 text-base text-foreground/80 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                  {t.hero.description}
                </p>
              </div>
            </main>
          </div>
        </div>
        <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
          <img
            className="h-56 w-full object-cover sm:h-72 md:h-96 lg:w-full lg:h-full opacity-80 mix-blend-lighten"
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80"
            alt="Cortex AI Team"
          />
        </div>
      </div>

      {/* Welcome Section */}
      <div className="py-16 bg-secondary overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8">
            <div>
              <h2 className="text-base text-accent font-semibold tracking-wide uppercase">{t.welcome.title}</h2>
              <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-foreground sm:text-4xl">
                {t.welcome.subtitle}
              </p>
              <p className="mt-4 max-w-2xl text-xl text-foreground/80">
                {t.welcome.description}
              </p>
              <div className="mt-6">
                <ul className="mt-4 space-y-3">
                  {t.welcome.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <span className="ml-3 text-base text-foreground/80">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="mt-12 relative text-base max-w-prose mx-auto lg:mt-0">
              <blockquote className="relative dark-card p-8">
                <div className="relative text-lg text-foreground/80 font-medium">
                  <svg
                    className="absolute top-0 left-0 transform -translate-x-3 -translate-y-2 h-8 w-8 text-accent/20"
                    fill="currentColor"
                    viewBox="0 0 32 32"
                    aria-hidden="true"
                  >
                    <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                  </svg>
                  <p className="relative">
                    {t.welcome.description}
                  </p>
                </div>
              </blockquote>
            </div>
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="py-16 bg-background overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base text-accent font-semibold tracking-wide uppercase">{t.mission.title}</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-foreground sm:text-4xl">
              {t.mission.subtitle}
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {t.mission.features.map((feature, index) => (
              <div key={index} className="dark-card p-6 hover:border-accent/50 transition-all duration-300">
                <div className="flex items-center mb-4">
                  {index === 0 && <Brain className="h-6 w-6 text-accent" />}
                  {index === 1 && <Target className="h-6 w-6 text-accent" />}
                  {index === 2 && <Shield className="h-6 w-6 text-accent" />}
                  <h3 className="ml-3 text-lg font-medium text-foreground">{feature.title}</h3>
                </div>
                <p className="text-foreground/80">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Founder Section */}
      <div className="bg-secondary">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:py-24 lg:px-8 lg:grid lg:grid-cols-2 lg:gap-x-8">
          <div>
            <h2 className="text-base text-accent font-semibold tracking-wide uppercase">{t.founder.title}</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-foreground sm:text-4xl">
              {t.founder.name}
            </p>
            <p className="mt-4 text-lg text-foreground/80">
              {t.founder.description}
            </p>
            <p className="mt-4 text-lg text-foreground/80">
              {t.founder.experience}
            </p>
            
            <div className="mt-6">
              <h3 className="text-xl font-bold text-foreground">{t.founder.vision.title}</h3>
              <p className="mt-2 text-lg text-foreground/80">
                {t.founder.vision.description}
              </p>
              <ul className="mt-4 space-y-3">
                {t.founder.vision.problems.map((problem, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <p className="ml-3 text-base text-foreground/80">{problem}</p>
                  </li>
                ))}
              </ul>
            </div>
            
            <blockquote className="mt-8 border-l-4 border-accent pl-4 italic">
              <p className="text-lg text-foreground/80">{t.founder.quote}</p>
              <footer className="mt-2 text-base font-medium text-foreground/60">
                — {t.founder.name}, {t.founder.role}
              </footer>
            </blockquote>
          </div>
          <div className="mt-12 lg:mt-0 lg:relative">
            <div className="relative mx-auto w-full rounded-lg shadow-lg overflow-hidden lg:max-w-md">
              <img
                className="w-full object-cover"
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=774&q=80"
                alt={`${t.founder.name}, ${t.founder.role}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <img
                      className="h-10 w-10 rounded-full border-2 border-white"
                      src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=774&q=80"
                      alt={t.founder.name}
                    />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-white">{t.founder.name}</p>
                    <p className="text-xs text-white/80">{t.founder.role}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="bg-background">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:py-24 lg:px-8">
          <div className="divide-y-2 divide-border">
            <div className="lg:grid lg:grid-cols-3 lg:gap-8">
              <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
                {t.contact.title}
              </h2>
              <div className="mt-8 grid grid-cols-1 gap-12 sm:grid-cols-2 sm:gap-x-8 sm:gap-y-12 lg:mt-0 lg:col-span-2">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-foreground">{t.contact.sections.support.title}</h3>
                  <dl className="mt-2 text-base text-foreground/80">
                    <div>
                      <dt className="sr-only">Email</dt>
                      <dd>{t.contact.sections.support.email}</dd>
                    </div>
                    <div className="mt-1">
                      <dt className="sr-only">Phone</dt>
                      <dd>{t.contact.sections.support.phone}</dd>
                    </div>
                  </dl>
                </div>
                <div>
                  <h3 className="text-lg leading-6 font-medium text-foreground">{t.contact.sections.sales.title}</h3>
                  <dl className="mt-2 text-base text-foreground/80">
                    <div>
                      <dt className="sr-only">Email</dt>
                      <dd>{t.contact.sections.sales.email}</dd>
                    </div>
                    <div className="mt-1">
                      <dt className="sr-only">Phone</dt>
                      <dd>{t.contact.sections.sales.phone}</dd>
                    </div>
                  </dl>
                </div>
                <div>
                  <h3 className="text-lg leading-6 font-medium text-foreground">{t.contact.sections.press.title}</h3>
                  <dl className="mt-2 text-base text-foreground/80">
                    <div>
                      <dt className="sr-only">Email</dt>
                      <dd>{t.contact.sections.press.email}</dd>
                    </div>
                    <div className="mt-1">
                      <dt className="sr-only">Phone</dt>
                      <dd>{t.contact.sections.press.phone}</dd>
                    </div>
                  </dl>
                </div>
                <div>
                  <h3 className="text-lg leading-6 font-medium text-foreground">{t.contact.sections.headquarters.title}</h3>
                  <dl className="mt-2 text-base text-foreground/80">
                    <div>
                      <dt className="sr-only">Address</dt>
                      <dd>
                        {t.contact.sections.headquarters.address.map((line, index) => (
                          <React.Fragment key={index}>
                            {line}
                            <br />
                          </React.Fragment>
                        ))}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
            <div className="mt-16 pt-16 lg:grid lg:grid-cols-3 lg:gap-8">
              <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
                {t.locations.title}
              </h2>
              <div className="mt-8 grid grid-cols-1 gap-12 sm:grid-cols-2 sm:gap-x-8 sm:gap-y-12 lg:mt-0 lg:col-span-2">
                {Object.entries(t.locations.offices).map(([key, office]) => (
                  <div key={key}>
                    <h3 className="text-lg leading-6 font-medium text-foreground">{office.city}</h3>
                    <div className="mt-2 text-base text-foreground/80">
                      {office.address.map((line, index) => (
                        <p key={index}>{line}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-purple-900 to-indigo-900">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <span className="block">{t.cta.title}</span>
            <span className="block text-indigo-100">{t.cta.subtitle}</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow">
              <Link
                to="/registro"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-purple-900 bg-white hover:bg-gray-50"
              >
                {t.cta.buttons.start}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
            <div className="ml-3 inline-flex rounded-md shadow">
              <a
                href="#"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-800 hover:bg-purple-900"
              >
                {t.cta.buttons.sales}
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;